
import { defineComponent } from 'vue';
import SingleMessageCard from '@/components/SingleMessageCard.vue';
import { mapGetters } from 'vuex';
import { getDirectContactTitle, isNullOrUndefined } from '@/shared/helpers';
import globalConstants from '@/shared/globalConstants';
import queries from '@/queries';
import Trailer from '@/types/Trailer';
import FbaApiError from '@/types/FbaApiError';

export default defineComponent({
    components: {
        SingleMessageCard,
    },
    data(): {
        trailer: Trailer | null;
    } {
        return {
            trailer: null,
        };
    },
    methods: {
        goBackToManualEntry() {
            this.$router.push({ name: 'EnterTrailer' });
        },
    },
    computed: {
        ...mapGetters({
            driver: 'getDriver',
            currentTrailer: 'selectedTrailer',
        }),
        trailerIsShopReviewStatus(): boolean {
            return (
                this.trailer?.company === 'swift' &&
                this.trailer?.status.toUpperCase() === 'I'
            );
        },
        trailerIsStolenStatus(): boolean {
            return (
                this.trailer?.company === 'swift' &&
                this.trailer?.status === '!'
            );
        },
        trailerNumber(): string | undefined {
            return this.trailer?.trailerNumber;
        },
        trailerStatusSubtitle(): string {
            if (this.trailerIsStolenStatus)
                return 'This trailer was previously stolen and has an open police report';
            else if (this.trailerIsShopReviewStatus)
                return 'This trailer is waiting on repairs';
            else if (
                this.trailer?.company === 'swift' &&
                this.trailer?.status === 'K'
            )
                return 'Currently under claims investigation';
            else if (this.trailer?.status === 'S') return 'Waiting on repairs';
            else if (
                this.trailer?.status === 'L' ||
                this.trailer?.cargoStatus ===
                    globalConstants.trailer.cargoStatuses.loaded
            )
                return 'If this is your trailer, please complete your L-Call';
            else if (
                !isNullOrUndefined(this.trailer?.reservation) &&
                this.trailer?.reservation?.driverId !== this.driver.id
            )
                return 'Held for another driver';
            else return '';
        },
        trailerStatusTitle(): string {
            if (this.trailerIsStolenStatus) return 'Stolen Trailer Status';
            else if (
                this.trailer?.status === 'S' ||
                this.trailerIsShopReviewStatus
            )
                return 'Shop Status';
            else if (
                (!isNullOrUndefined(this.trailer?.reservation) &&
                    this.trailer?.reservation?.driverId !== this.driver.id &&
                    this.trailer?.status !== 'S') ||
                (this.trailer?.company === 'swift' &&
                    this.trailer?.status === 'K')
            )
                return 'Trailer Unavailable';
            else if (
                this.trailer?.status === 'L' ||
                this.trailer?.cargoStatus ===
                    globalConstants.trailer.cargoStatuses.loaded
            )
                return 'Trailer Loaded';
            else return 'Trailer Unavailable';
        },
        detailsDescription(): string {
            if (this.trailerIsStolenStatus) {
                return `Please contact the security team at <a style="text-decoration: none; color: #615f5a;" href="tel:${process.env.VUE_APP_SWIFT_SECURITY_TEAM}">${process.env.VUE_APP_SWIFT_SECURITY_TEAM}</a> and your DL before taking this trailer.`;
            } else if (
                this.trailer?.status === 'L' ||
                (this.trailer?.cargoStatus ===
                    globalConstants.trailer.cargoStatuses.loaded &&
                    ((this.trailer?.company === 'swift' &&
                        this.trailer?.status !== 'K') ||
                        this.trailer?.company === 'knight'))
            ) {
                return `Please use Trailer Solutions to find another trailer or call your ${getDirectContactTitle(
                    this.trailer?.company
                )} if you have any questions.`;
            } else {
                return 'Please call your dispatcher if you have any questions or believe this is incorrect.';
            }
        },
    },
    async mounted() {
        let trailerNum = this.currentTrailer?.trailerNumber;
        let trailerCompany = this.currentTrailer?.company;

        if (isNullOrUndefined(this.currentTrailer)) {
            trailerNum = this.$route.query.trailerNumber;
            trailerCompany = this.$route.query.trailerCompany;
        }

        const result = await queries.getTrailerById(trailerNum, trailerCompany);

        if (result instanceof FbaApiError) {
            console.warn('Trailer lookup failed: ', result);
            return;
        } else this.trailer = result;
    },
});
